import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Stack,
  IconButton,
  Button,
  Chip,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import EditRounded from "@mui/icons-material/EditRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import SouthWestRounded from "@mui/icons-material/SouthWestRounded";
import NorthEastRounded from "@mui/icons-material/NorthEastRounded";
import PaymentsOutlined from "@mui/icons-material/PaymentsOutlined";
import CheckRounded from "@mui/icons-material/CheckRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import { useTheme } from "@mui/material";
import { reverseTranslate, translate } from "../utils/currency";
import { IMerchantRateCard } from "../utils/types";
import { transactionTypes } from "../utils/utils";
import { changeMerchRates, deleteRate } from "../utils/api";

const operationLabels: Record<string, string> = {
  deposit: "Пополнение",
  payout: "Выплата",
  cashin: "Cash in",
  withdrawal: "Вывод",
};

const MerchRateCard: React.FC<IMerchantRateCard> = ({
  data,
  currencies,
  methods,
  dialogRef,
  merchantID,
  fetchData,
}) => {
  const { operation_type, payment_method, currency, minsum, maxsum, rate } =
    data;
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);

  const createInitialFormData = () => ({
    rate,
    payment_method,
    currency: translate(currency.toUpperCase()),
    operation_type,
    minsum,
    maxsum,
  });

  const [formData, setFormData] = useState(createInitialFormData);

  const operationStyles = {
    deposit: {
      color: theme.palette.primary.main,
      soft: "rgba(99, 91, 255, 0.12)",
      icon: SouthWestRounded,
    },
    payout: {
      color: theme.palette.success.main,
      soft: "rgba(46, 174, 128, 0.12)",
      icon: NorthEastRounded,
    },
    cashin: {
      color: theme.palette.warning.main,
      soft: "rgba(245, 166, 35, 0.12)",
      icon: PaymentsOutlined,
    },
    withdrawal: {
      color: theme.palette.error.main,
      soft: "rgba(239, 83, 80, 0.12)",
      icon: NorthEastRounded,
    },
  } as const;

  const activeOperation =
    operationStyles[
      formData.operation_type as keyof typeof operationStyles
    ] ?? operationStyles.deposit;
  const OperationIcon = activeOperation.icon;

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      minHeight: 46,
      borderRadius: "11px",
      backgroundColor: "action.hover",
    },
    "& .MuiInputBase-input, & .MuiSelect-select": {
      fontSize: "14px",
    },
    "& .MuiInputLabel-root": {
      fontSize: "13px",
    },
  } as const;

  const handleCancel = () => {
    setFormData(createInitialFormData());
    setIsEditing(false);
  };

  const handleSave = async () => {
    await changeMerchRates({
      merchant_id: Number(merchantID),
      currency: reverseTranslate(formData.currency).toLowerCase(),
      payment_method: formData.payment_method ?? "",
      rate: Number(formData.rate),
      operation_type: formData.operation_type?.toLowerCase() ?? "",
      minsum: Number(formData.minsum),
      maxsum: Number(formData.maxsum),
    });
    dialogRef.current?.closeDialog();
    await fetchData?.();
    setIsEditing(false);
  };

  const handleDelete = async () => {
    await deleteRate({
      merchant_id: Number(merchantID),
      currency: currency.toLowerCase(),
      payment_method,
      operation_type,
      minsum: Number(formData.minsum),
      maxsum: Number(formData.maxsum),
    });
    dialogRef.current?.closeDialog();
    await fetchData?.();
  };

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: "16px",
        border: "1px solid",
        borderColor: isEditing ? activeOperation.color : "divider",
        borderLeft: `4px solid ${activeOperation.color}`,
        p: { xs: 1.5, sm: 1.75 },
        minWidth: 0,
        backgroundColor: "background.paper",
        backgroundImage: "none",
        boxShadow: isEditing
          ? `0 14px 32px ${activeOperation.soft}`
          : "none",
        transition:
          "border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
        "&:hover": {
          borderColor: activeOperation.color,
          transform: isEditing ? "none" : "translateY(-2px)",
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={1}
      >
        <Stack direction="row" alignItems="center" gap={1.1} minWidth={0}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "11px",
              display: "grid",
              placeItems: "center",
              color: activeOperation.color,
              backgroundColor: activeOperation.soft,
              flexShrink: 0,
            }}
          >
            <OperationIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box minWidth={0}>
            <Typography variant="subtitle2" fontWeight={750} noWrap>
              {operationLabels[formData.operation_type] ??
                formData.operation_type}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {translate(formData.payment_method)}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" gap={0.25} flexShrink={0}>
          {!isEditing && (
            <Tooltip title="Редактировать">
              <IconButton
                size="small"
                onClick={() => setIsEditing(true)}
                sx={{ color: "text.secondary" }}
              >
                <EditRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Удалить ставку">
            <IconButton
              size="small"
              onClick={handleDelete}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  color: "error.main",
                  backgroundColor: "rgba(239, 83, 80, 0.10)",
                },
              }}
            >
              <DeleteOutlineRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {isEditing ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: "12px",
            mt: 2,
          }}
        >
          <TextField
            select
            size="small"
            label="Операция"
            value={formData.operation_type}
            onChange={(event) =>
              setFormData((previous) => ({
                ...previous,
                operation_type: event.target.value,
              }))
            }
            SelectProps={{ MenuProps: { disableScrollLock: true } }}
            sx={fieldSx}
          >
            {transactionTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {operationLabels[type] ?? type}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            label="Ставка"
            type="number"
            value={formData.rate}
            onChange={(event) =>
              setFormData((previous) => ({
                ...previous,
                rate: event.target.value,
              }))
            }
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            sx={fieldSx}
          />

          <TextField
            select
            size="small"
            label="Метод оплаты"
            value={formData.payment_method}
            onChange={(event) =>
              setFormData((previous) => ({
                ...previous,
                payment_method: event.target.value,
              }))
            }
            SelectProps={{ MenuProps: { disableScrollLock: true } }}
            sx={fieldSx}
          >
            {Object.entries(methods).map(([key, value]) => (
              <MenuItem key={key} value={value}>
                {translate(value)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Валюта"
            value={formData.currency}
            onChange={(event) =>
              setFormData((previous) => ({
                ...previous,
                currency: event.target.value,
              }))
            }
            SelectProps={{ MenuProps: { disableScrollLock: true } }}
            sx={fieldSx}
          >
            {Object.entries(currencies).map(([key, value]) => (
              <MenuItem key={key} value={value.label}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <img
                    src={value.img}
                    alt=""
                    width={20}
                    height={20}
                    style={{ objectFit: "contain" }}
                  />
                  {value.label}
                </Stack>
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            label="Сумма от"
            type="number"
            value={formData.minsum}
            onChange={(event) =>
              setFormData((previous) => ({
                ...previous,
                minsum: Number(event.target.value),
              }))
            }
            sx={fieldSx}
          />

          <TextField
            size="small"
            label="Сумма до"
            type="number"
            value={formData.maxsum}
            onChange={(event) =>
              setFormData((previous) => ({
                ...previous,
                maxsum: Number(event.target.value),
              }))
            }
            sx={fieldSx}
          />

          <Stack
            direction="row"
            justifyContent="flex-end"
            gap={1}
            sx={{ gridColumn: "1 / -1", pt: 0.5 }}
          >
            <Button
              size="small"
              color="inherit"
              startIcon={<CloseRounded />}
              onClick={handleCancel}
            >
              Отмена
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<CheckRounded />}
              onClick={handleSave}
            >
              Сохранить
            </Button>
          </Stack>
        </Box>
      ) : (
        <Box sx={{ mt: 2 }}>
          <Stack
            direction="row"
            alignItems="flex-end"
            justifyContent="space-between"
            gap={1}
          >
            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ fontSize: "10px", letterSpacing: ".08em" }}
              >
                Ставка
              </Typography>
              <Typography
                sx={{
                  mt: "-3px",
                  color: activeOperation.color,
                  fontSize: "25px",
                  lineHeight: 1.15,
                  fontWeight: 800,
                }}
              >
                {rate}%
              </Typography>
            </Box>
            <Chip
              label={currency.toUpperCase()}
              size="small"
              sx={{ height: 25, fontSize: "11px", fontWeight: 700 }}
            />
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr auto 1fr",
              alignItems: "center",
              gap: 1,
              mt: 1.5,
              p: "9px 11px",
              borderRadius: "11px",
              backgroundColor: "action.hover",
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                От
              </Typography>
              <Typography variant="body2" fontWeight={650}>
                {minsum.toLocaleString("ru-RU")}
              </Typography>
            </Box>
            <Typography color="text.disabled">—</Typography>
            <Box sx={{ textAlign: "right" }}>
              <Typography variant="caption" color="text.secondary">
                До
              </Typography>
              <Typography variant="body2" fontWeight={650}>
                {maxsum.toLocaleString("ru-RU")}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MerchRateCard;
